/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react'
import api from '../../services/axios'
import styled from 'styled-components'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { CiSaveDown1 } from 'react-icons/ci'

import GidIcon from '../../assets/icons/gid-logo.png'
import ArrowRed from '../../assets/icons/arrowred.png'
import ArrowGree from '../../assets/icons/Arrowgreen.png'
import Neutro from '../../assets/icons/Minus.png'

import { monthNames } from '../../components/Filters/MonthFilter'
import { useEstoqueFilter } from '../../context/FiltersContext/EstoqueFilter'
import StockTotalChart from './Graph'
import { Skeleton } from '../../components/Skeletron'
import { DashMain } from '../SomarizadorScreen/styles'
import {
  IoInformationCircleOutline,
  IoCalendarOutline,
  IoBusinessOutline,
  IoLocationOutline,
  IoPricetagOutline,
  IoCubeOutline,
} from 'react-icons/io5'
import { AiOutlineClose } from 'react-icons/ai'
import HierarchyCards, { PerformanceCategoria } from './HierarchyCards'
import DistributorStockAnalysis from './DistributorStockAnalysis'

const getBase64ImageFromUrl = async (imageUrl: string) => {
  try {
    const res = await fetch(imageUrl)
    const blob = await res.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Erro ao converter logo:', error)
    return imageUrl
  }
}

// Tipagem do retorno da API de Dashboard de Estoque Otimizada
interface DashboardData {
  totalData: { atual: number; anterior: number }
  topItems: {
    topFamily: { name: string; volume: number }
    topProduct: { name: string; volume: number }
  }
  diasEstoque: number
  totalSellout3M?: number
  comparativoFamilias: Array<{
    categoria: string
    atual: number
    anterior: number
    vendaMedia: number
    percentual: number
    tendencia: 'UP' | 'DOWN' | 'NEUTRAL'
  }>
  performanceHierarquia?: PerformanceCategoria[]
  tabelaDistribuidores: Array<{
    dist_id: number
    nome: string
    produtoMaior: string
    estoque: number
    sellout3M?: number
    dias: number
    mesesHistorico?: number
  }>
  produtoMaiorEstoque: {
    nome: string
    estoque: number
    mediaTotal: number
    dias: string
  } | null
  listaItensParados: Array<{
    nome: string
    estoque: number
    dias: string
  }>
}

export default function EstoqueScreen() {
  const {
    year: selectedYear,
    month: selectedMonth,
    distributorIds,
    regional,
    gestor,
    marca,
    categoria,
  } = useEstoqueFilter()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DashboardData | null>(null)
  const [showModalInfo, setShowModalInfo] = useState(false)
  const dashRef = useRef<HTMLDivElement>(null)

  const distsKey = distributorIds.join(',')

  // --- CARREGAMENTO DE DADOS OTIMIZADO ---
  useEffect(() => {
    loadDashboard()
  }, [
    selectedMonth,
    selectedYear,
    distsKey,
    regional,
    gestor,
    marca,
    categoria,
  ])

  const loadDashboard = async () => {
    setLoading(true)
    try {
      const monthNumber = Number(selectedMonth) + 1

      const response = await api.get('/estoque/dashboard-analise', {
        params: {
          month: monthNumber,
          year: selectedYear,
          distributorIds: distributorIds.length > 0 ? distributorIds.join(',') : undefined,
          regional: regional || undefined,
          gestor: gestor || undefined,
          marca: marca || undefined,
          categoria: categoria || undefined,
        },
      })
      setData(response.data)
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard de estoque:', error)
    } finally {
      setLoading(false)
    }
  }

  // --- PROCESSAMENTO VISUAL ---
  const getIconForTrend = (trend: string) => {
    if (trend === 'UP') return ArrowGree
    if (trend === 'DOWN') return ArrowRed
    return Neutro
  }

  const exportToPDF = async () => {
    if (!data) return
    setLoading(true)
    try {
      const pdf = new (jsPDF as any)('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      const originalAddPage = pdf.addPage.bind(pdf)
      pdf.addPage = function () {
        originalAddPage()
        pdf.setFillColor(0, 29, 83)
        pdf.rect(0, 0, pageWidth, pageHeight, 'F')
        return pdf
      }

      pdf.setFillColor(0, 29, 83)
      pdf.rect(0, 0, pageWidth, pageHeight, 'F')

      const minhaLogoBase64 = (await getBase64ImageFromUrl(GidIcon)) as string
      pdf.addImage(minhaLogoBase64, 'PNG', 10, 10, 30, 20)

      pdf.setTextColor(255, 255, 255)
      pdf.setFontSize(16)
      pdf.text('RELATÓRIO DE ANÁLISE DE ESTOQUE', pageWidth / 2, 20, { align: 'center' })

      pdf.setFontSize(10)
      pdf.text(
        `Período: ${monthNames[selectedMonth]} / ${selectedYear}`,
        pageWidth / 2,
        28,
        { align: 'center' },
      )
      pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, pageWidth / 2, 34, {
        align: 'center',
      })

      pdf.setFillColor(166, 193, 11)
      pdf.rect(0, 40, pageWidth, 2, 'F')

      pdf.setTextColor(255, 255, 255)
      pdf.setDrawColor(166, 193, 11)
      pdf.setLineWidth(0.5)

      const startY = 48
      const boxHeight = 28
      const boxWidth = 58

      // Quadro 1: Família
      pdf.rect(12, startY, boxWidth, boxHeight)
      pdf.setFontSize(9)
      pdf.setFont('helvetica', 'bold')
      pdf.text('FAMÍLIA MAIOR VOL.', 15, startY + 7)
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8)
      const famName = pdf.splitTextToSize(data.topItems.topFamily.name, boxWidth - 6)
      pdf.text(famName, 15, startY + 14)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(10)
      pdf.text(`${Math.round(data.topItems.topFamily.volume).toLocaleString('pt-BR')} cx`, 15, startY + 23)

      // Quadro 2: Produto
      const xQ2 = 12 + boxWidth + 5
      pdf.rect(xQ2, startY, boxWidth, boxHeight)
      pdf.setFontSize(9)
      pdf.text('PRODUTO MAIOR VOL.', xQ2 + 3, startY + 7)
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8)
      const prodName = pdf.splitTextToSize(data.topItems.topProduct.name, boxWidth - 6)
      pdf.text(prodName, xQ2 + 3, startY + 14)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(10)
      pdf.text(
        `${Math.round(data.topItems.topProduct.volume).toLocaleString('pt-BR')} cx`,
        xQ2 + 3,
        startY + 23,
      )

      // Quadro 3: Dias
      const xQ3 = xQ2 + boxWidth + 5
      pdf.rect(xQ3, startY, boxWidth, boxHeight)
      pdf.setFontSize(9)
      pdf.text('DIAS DE ESTOQUE MÉDIO', xQ3 + 3, startY + 7)
      pdf.setFontSize(15)
      pdf.text(
        `${data.diasEstoque > 0 ? data.diasEstoque.toFixed(0) : '0'} Dias`,
        xQ3 + 3,
        startY + 19,
      )

      let currentYForTable = 84

      // Tabela: Famílias
      const tableFamData = data.comparativoFamilias.map((item) => [
        item.categoria,
        item.atual.toLocaleString('pt-BR', { maximumFractionDigits: 1 }),
        item.anterior.toLocaleString('pt-BR', { maximumFractionDigits: 1 }),
        item.vendaMedia.toLocaleString('pt-BR', { maximumFractionDigits: 1 }),
        item.tendencia === 'UP'
          ? `+${item.percentual}%`
          : item.tendencia === 'DOWN'
            ? `-${item.percentual}%`
            : `${item.percentual}%`,
      ])

      autoTable(pdf, {
        startY: currentYForTable,
        head: [['Família', 'Estoque Atual (cx)', 'Mês Anterior (cx)', 'Venda Média 3M (cx)', 'Variação (%)']],
        body: tableFamData,
        headStyles: {
          fillColor: [166, 193, 11],
          textColor: [0, 29, 83],
          fontStyle: 'bold',
          halign: 'center',
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'center' },
        },
        bodyStyles: {
          fillColor: [0, 29, 83],
          textColor: [255, 255, 255],
          lineColor: [20, 50, 110],
          lineWidth: 0.1,
        },
        alternateRowStyles: { fillColor: [0, 40, 100] },
        styles: { fontSize: 8, cellPadding: 3 },
        didDrawPage: () => {
          pdf.saveGraphicsState()
          pdf.setGState(new pdf.GState({ opacity: 0.05 }))
          const watermarkWidth = 130
          const watermarkHeight = 100
          const centerX = (pageWidth - watermarkWidth) / 2
          const centerY = (pageHeight - watermarkHeight) / 2
          pdf.addImage(minhaLogoBase64, 'PNG', centerX, centerY, watermarkWidth, watermarkHeight)
          pdf.restoreGraphicsState()
        },
      })

      currentYForTable = (pdf as any).lastAutoTable.finalY + 12

      // Tabela: Distribuidores (Comparativo 1 para 1)
      const tableDistDetalheData = data.tabelaDistribuidores.map((dist) => [
        dist.nome,
        dist.produtoMaior,
        dist.estoque.toLocaleString('pt-BR'),
        (dist.sellout3M ?? 0).toLocaleString('pt-BR'),
        dist.dias.toString(),
      ])

      const totalEstoqueResumo = data.tabelaDistribuidores.reduce(
        (acc, curr) => acc + curr.estoque,
        0,
      )
      const totalSelloutResumo = data.tabelaDistribuidores.reduce(
        (acc, curr) => acc + (curr.sellout3M ?? 0),
        0,
      )

      tableDistDetalheData.push([
        'Total Geral',
        '-',
        totalEstoqueResumo.toLocaleString('pt-BR'),
        totalSelloutResumo.toLocaleString('pt-BR'),
        data.diasEstoque > 0 ? Math.round(data.diasEstoque).toString() : '0',
      ])

      autoTable(pdf, {
        startY: currentYForTable,
        head: [['Distribuidor', 'Produto c/ Maior Estoque', 'Estoque Atual (Cx)', 'Sellout 3M (Cx)', 'Dias']],
        body: tableDistDetalheData,
        headStyles: {
          fillColor: [166, 193, 11],
          textColor: [0, 29, 83],
          fontStyle: 'bold',
          halign: 'center',
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'left' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' },
        },
        bodyStyles: {
          fillColor: [0, 29, 83],
          textColor: [255, 255, 255],
          lineColor: [20, 50, 110],
          lineWidth: 0.1,
        },
        alternateRowStyles: { fillColor: [0, 40, 100] },
        styles: { fontSize: 8, cellPadding: 3 },
        didParseCell: function (cellData) {
          if (cellData.row.index === tableDistDetalheData.length - 1) {
            cellData.cell.styles.fontStyle = 'bold'
            cellData.cell.styles.textColor = [166, 193, 11]
          }
        },
      })

      pdf.save(`Relatorio_Estoque_${monthNames[selectedMonth]}_${selectedYear}.pdf`)
    } catch (error) {
      console.error('Erro ao gerar PDF', error)
    } finally {
      setLoading(false)
    }
  }


  const hierarquiaData: PerformanceCategoria[] = React.useMemo(() => {
    if (data?.performanceHierarquia && data.performanceHierarquia.length > 0) {
      return data.performanceHierarquia
    }

    if (!data?.comparativoFamilias) return []

    const fallbackFamilias = data.comparativoFamilias.map((f) => {
      const cob = f.vendaMedia > 0 ? f.atual / f.vendaMedia : 0
      const dias = f.vendaMedia > 0 ? Math.round(f.atual / (f.vendaMedia / 30)) : 0
      let status: 'Equilibrado' | 'Excesso' | 'Ruptura' | 'Sem Giro' = 'Equilibrado'
      if (f.atual === 0 && f.vendaMedia === 0) status = 'Sem Giro'
      else if (f.vendaMedia === 0 && f.atual > 0) status = 'Sem Giro'
      else if (cob > 3) status = 'Excesso'
      else if (cob < 0.5 && f.vendaMedia > 0) status = 'Ruptura'

      return {
        familia: f.categoria,
        atual: f.atual,
        anterior: f.anterior,
        sellout3M: f.vendaMedia * 3,
        vendaMedia: f.vendaMedia,
        percentual: f.percentual,
        tendencia: f.tendencia,
        cobertura: cob,
        dias,
        status,
        totalSkus: 0,
        skus: [],
      }
    })

    const totalAtual = fallbackFamilias.reduce((acc, curr) => acc + curr.atual, 0)
    const totalAnt = fallbackFamilias.reduce((acc, curr) => acc + curr.anterior, 0)
    const totalVM = fallbackFamilias.reduce((acc, curr) => acc + curr.vendaMedia, 0)
    const diff = totalAtual - totalAnt
    const perc = totalAnt > 0 ? Math.abs(Number(((diff / totalAnt) * 100).toFixed(1))) : 100
    const cobGeral = totalVM > 0 ? totalAtual / totalVM : 0

    return [
      {
        categoria: 'Geral',
        atual: totalAtual,
        anterior: totalAnt,
        sellout3M: totalVM * 3,
        vendaMedia: totalVM,
        percentual: perc,
        tendencia: (diff > 0 ? 'UP' : diff < 0 ? 'DOWN' : 'NEUTRAL') as any,
        cobertura: cobGeral,
        dias: Math.round(cobGeral * 30),
        status: cobGeral > 3 ? 'Excesso' : cobGeral < 0.5 ? 'Ruptura' : 'Equilibrado',
        totalFamilias: fallbackFamilias.length,
        totalSkus: 0,
        familias: fallbackFamilias,
      },
    ]
  }, [data])

  if (loading || !data) return <Skeleton />

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0px 20px' }}>
        <button
          onClick={exportToPDF}
          title='Exportar PDF'
          style={{
            position: 'fixed',
            bottom: 80,
            padding: '10px 20px',
            backgroundColor: '#001D53',
            color: 'white',
            border: 'none',
            height: 60,
            borderRadius: 20,
            cursor: 'pointer',
            zIndex: 9999999,
            fontFamily: 'Montserrat',
            fontWeight: 'bold',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.2)',
          }}
        >
          <CiSaveDown1 style={{ color: '#ffffff', fontSize: 24 }} />
        </button>
      </div>

      <DashMain ref={dashRef}>
        {/* CABEÇALHO COM BADGES DE FILTROS ATIVOS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0 10px', flexWrap: 'wrap', gap: '15px' }}>
          <EstoqueTitleGroup>
            <h2>Análise de Estoque</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
              <FilterChip>
                <IoCalendarOutline size={13} style={{ marginRight: 5, color: '#a6c10b' }} />
                {monthNames[selectedMonth]} / {selectedYear}
              </FilterChip>
              {distributorIds.length > 0 && (
                <FilterChip style={{ borderColor: '#a6c10b', color: '#a6c10b' }}>
                  <IoBusinessOutline size={13} style={{ marginRight: 5, color: '#a6c10b' }} />
                  {distributorIds.length} distribuidor(es) selecionado(s)
                </FilterChip>
              )}
              {regional && (
                <FilterChip>
                  <IoLocationOutline size={13} style={{ marginRight: 5, color: '#a6c10b' }} />
                  {regional}
                </FilterChip>
              )}
              {marca && (
                <FilterChip>
                  <IoPricetagOutline size={13} style={{ marginRight: 5, color: '#a6c10b' }} />
                  {marca}
                </FilterChip>
              )}
              {categoria && (
                <FilterChip>
                  <IoCubeOutline size={13} style={{ marginRight: 5, color: '#a6c10b' }} />
                  {categoria}
                </FilterChip>
              )}
            </div>
          </EstoqueTitleGroup>

          <Badge>
            Dias de Estoque Médio
            <p style={{ fontSize: 24, textAlign: 'center', margin: '5px 0 0 0' }}>
              {data.diasEstoque > 0 ? data.diasEstoque.toFixed(0) : '0'}
            </p>
          </Badge>
        </div>

        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <SummaryQuadro>
            <h4>Família com Maior Volume</h4>
            <p className='quadro-val'>{data.topItems.topFamily.name}</p>
            <p className='quadro-sub'>
              {Math.round(data.topItems.topFamily.volume).toLocaleString('pt-BR')} cx
            </p>
          </SummaryQuadro>

          <SummaryQuadro>
            <h4>Produto com Maior Volume</h4>
            <p className='quadro-val'>{data.topItems.topProduct.name}</p>
            <p className='quadro-sub'>
              {Math.round(data.topItems.topProduct.volume).toLocaleString('pt-BR')} cx
            </p>
          </SummaryQuadro>
        </div>

        <StockTotalChart atual={data.totalData.atual} anterior={data.totalData.anterior} />

        {/* CARDS HIERÁRQUICOS: CATEGORIA -> FAMÍLIA -> SKU */}
        <HierarchyCards
          data={hierarquiaData}
          onOpenInfoModal={() => setShowModalInfo(true)}
        />

        {/* ESTOQUE POR DISTRIBUIDOR (ANÁLISE 1 PARA 1) OTIMIZADA */}
        <DistributorStockAnalysis
          data={data.tabelaDistribuidores || []}
          diasEstoqueGeral={data.diasEstoque}
          totalSellout3MGeral={data.totalSellout3M}
        />
      </DashMain>

      {showModalInfo && (
        <ModalOverlay onClick={() => setShowModalInfo(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Critérios de Performance e Dias de Estoque</h3>
              <AiOutlineClose
                style={{ cursor: 'pointer', fontSize: '20px' }}
                onClick={() => setShowModalInfo(false)}
              />
            </ModalHeader>
            <ModalBody>
              <p>
                O cálculo de <strong>Dias de Estoque</strong> reflete a quantidade de dias que o estoque atual suporta considerando a média de vendas diária dos meses com histórico (até 3 meses):
              </p>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '12px', margin: '10px 0' }}>
                Média Diária = Sellout do período / Quantidade de dias com histórico (30d para 1M, 60d para 2M, 90d para 3M)<br />
                Dias de Estoque = Estoque Atual (cx) / Média Diária
              </div>

              <p style={{ marginTop: '15px' }}>
                A <strong>Performance por Família</strong> categoriza o estoque em relação ao giro mensal:
              </p>

              <div className='criteria-item'>
                <span className='dot red' />
                <div>
                  <strong>Excesso (Acima de 3 meses):</strong> Indica que o estoque atual é
                  suficiente para cobrir mais de 3 meses de vendas. Risco de capital parado.
                </div>
              </div>

              <div className='criteria-item'>
                <span className='dot green' />
                <div>
                  <strong>Equilibrado (0.5 a 3 meses):</strong> Nível de estoque saudável,
                  garantindo o atendimento sem excessos.
                </div>
              </div>

              <div className='criteria-item'>
                <span className='dot orange' />
                <div>
                  <strong>Ruptura (Abaixo de 0.5 mês):</strong> Estoque muito baixo em relação à
                  demanda. Alto risco de falta de produto para venda.
                </div>
              </div>

              <div style={{ marginTop: '20px', fontSize: '12px', color: '#8e9aaf' }}>
                * O estoque atual considera o último snapshot do distribuidor dentro do mês filtrado.
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}

const EstoqueTitleGroup = styled.div`
  border-left: 4px solid #a6c10b;
  padding-left: 10px;
  margin-bottom: 15px;

  h2 {
    font-size: 20px;
    color: ${(props) => props.theme.titleColor};
    margin: 0;
    font-weight: 700;
  }
`

const SummaryQuadro = styled.div`
  flex: 1 1 300px;
  background-color: ${(props) => props.theme.cardBackground};
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 12px;
  padding: 20px;
  border-left: 5px solid #a6c10b;
  box-shadow: ${(props) => props.theme.shadow};
  display: flex;
  flex-direction: column;
  justify-content: center;

  h4 {
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#a6c10b')};
    margin: 0 0 10px 0;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    font-family: 'Montserrat', sans-serif;
  }

  .quadro-val {
    color: ${(props) => props.theme.textPrimary};
    font-size: 18px;
    font-weight: bold;
    margin: 0 0 5px 0;
    font-family: 'Montserrat', sans-serif;
    word-break: break-word;
  }

  .quadro-sub {
    color: ${(props) => props.theme.textSecondary};
    font-size: 14px;
    margin: 0;
    font-family: 'Montserrat', sans-serif;
  }
`

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.08)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.2)')};
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 1px 4px rgba(0,0,0,0.06)' : 'none')};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  line-height: 1.2;
`


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000000;
`

const ModalContent = styled.div`
  background: #001d53;
  width: 90%;
  max-width: 520px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  font-family: 'Montserrat', sans-serif;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  color: #fff;
  h3 {
    margin: 0;
    font-size: 17px;
  }
`

const ModalBody = styled.div`
  color: #fff;
  font-size: 13px;
  line-height: 1.6;

  .criteria-item {
    display: flex;
    gap: 15px;
    margin-top: 15px;
    align-items: flex-start;
  }

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 5px;
    flex-shrink: 0;
  }
  .red {
    background: #ff4d4f;
    box-shadow: 0 0 10px #ff4d4f;
  }
  .green {
    background: #a6c10b;
    box-shadow: 0 0 10px #a6c10b;
  }
  .orange {
    background: #ffa940;
    box-shadow: 0 0 10px #ffa940;
  }
`


const Badge = styled.span`
  background: rgba(166, 193, 11, 0.1);
  color: #a6c10b;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 11px;
  border: 1px solid rgba(166, 193, 11, 0.3);
  font-weight: bold;

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`

const quadroStyle = {
  flex: '1 1 300px',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  borderRadius: '12px',
  padding: '20px',
  borderLeft: '5px solid #a6c10b',
  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
}
const quadroTitle = {
  color: '#a6c10b',
  margin: '0 0 10px 0',
  fontSize: '12px',
  textTransform: 'uppercase' as const,
  fontFamily: 'Montserrat, sans-serif',
}
const quadroValue = {
  color: '#fff',
  fontSize: '18px',
  fontWeight: 'bold',
  margin: '0 0 5px 0',
  fontFamily: 'Montserrat, sans-serif',
  wordBreak: 'break-word' as const,
}
const quadroSubValue = {
  color: '#dadada',
  fontSize: '14px',
  margin: 0,
  fontFamily: 'Montserrat, sans-serif',
}
